import React, { useState, useEffect, useCallback, useMemo } from "react";
import useImagePreloader from "../hooks/useImagePreloader";
import LoadingOverlay from "../components/LoadingOverlay";
import { useTranslation } from "react-i18next";
import useDocumentTitle from "../hooks/useDocumentTitle";

import PVTHero from "../components/PVT/PVTHero";
import PVTTechnology from "../components/PVT/PVTTechnology";
import PVTBenefits from "../components/PVT/PVTBenefits";
import PVTSpecifications from "../components/PVT/PVTSpecifications";
import ImageModal from "../components/PVT/ImageModal";

import pvtImage from "/images/PVT/PFZ_201.webp";
import pvtImage1 from "/images/PVT/PFZ_202.webp";
import pvtImage2 from "/images/PVT/PFZ_203.webp";
import pvtImage3 from "/images/PVT/PFZ_204.webp";
import pvtImage4 from "/images/PVT/PFZ_205.webp";

import pvtHeroImage from "/images/PVT/PFZ_201.webp";
import pvtTechImage1 from "/images/PVT/PFZ_101.webp";
import pvtTechImage2 from "/images/PVT/PFZ_102.webp";
import pvtTechImage3 from "/images/PVT/PFZ_103.webp";
import pvtTechImage4 from "/images/PVT/PFZ_104.webp";
import pvtTechImage5 from "/images/PVT/PFZ_105.webp";
import pvtSpecImage1 from "/images/PVT/PFZ_202.webp";
import pvtSpecImage2 from "/images/PVT/PFZ_203.webp";
import pvtSpecImage3 from "/images/PVT/PFZ_204.webp";
import pvtSpecImage4 from "/images/PVT/PFZ_205.webp";

const PVT = React.memo(function PVT() {
  useDocumentTitle("pvt");
  const { t } = useTranslation();
  const pvtDetailData = t("components.pvt.pvtDetailPanel", {
    returnObjects: true,
  });

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [autoRotateEnabled, setAutoRotateEnabled] = useState(true);

  const panelImages = useMemo(
    () => [
      { src: pvtImage, alt: pvtDetailData.panelImages.frontView },
      { src: pvtImage1, alt: pvtDetailData.panelImages.sideView },
      { src: pvtImage2, alt: pvtDetailData.panelImages.installation1 },
      { src: pvtImage3, alt: pvtDetailData.panelImages.installation2 },
      { src: pvtImage4, alt: pvtDetailData.panelImages.rearView },
    ],
    [pvtDetailData.panelImages]
  );

  const setImageIndex = useCallback(
    (index) => {
      if (index >= 0 && index < panelImages.length) {
        setCurrentImageIndex(index);
        setAutoRotateEnabled(false);
        setTimeout(() => setAutoRotateEnabled(true), 10000);
      }
    },
    [panelImages.length]
  );

  const nextImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === panelImages.length - 1 ? 0 : prevIndex + 1
    );
    setAutoRotateEnabled(false);
    setTimeout(() => setAutoRotateEnabled(true), 10000);
  }, [panelImages.length]);

  const prevImage = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? panelImages.length - 1 : prevIndex - 1
    );
    setAutoRotateEnabled(false);
    setTimeout(() => setAutoRotateEnabled(true), 10000);
  }, [panelImages.length]);

  const openImageModal = useCallback(() => {
    setIsImageModalOpen(true);
    document.body.style.overflow = "hidden";
    setAutoRotateEnabled(false);
  }, []);

  const closeImageModal = useCallback(() => {
    setIsImageModalOpen(false);
    document.body.style.overflow = "auto";
    setTimeout(() => setAutoRotateEnabled(true), 1000);
  }, []);

  useEffect(() => {
    if (autoRotateEnabled && !isImageModalOpen) {
      const timer = setTimeout(() => {
        setCurrentImageIndex((prevIndex) =>
          prevIndex === panelImages.length - 1 ? 0 : prevIndex + 1
        );
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [
    currentImageIndex,
    isImageModalOpen,
    autoRotateEnabled,
    panelImages.length,
  ]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  const imagesToLoad = [
    pvtHeroImage,
    pvtTechImage1,
    pvtTechImage2,
    pvtTechImage3,
    pvtTechImage4,
    pvtTechImage5,
    pvtSpecImage1,
    pvtSpecImage2,
    pvtSpecImage3,
    pvtSpecImage4,
  ];

  const loading = useImagePreloader(imagesToLoad);

  return (
    <>
      {loading && <LoadingOverlay />}
      <main>
        <section
          className="py-16 bg-gradient-to-b from-black to-gray-900 text-white overflow-hidden mt-4"
          id="safePad"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <PVTHero
            detailData={pvtDetailData}
            panelImages={panelImages}
            currentImageIndex={currentImageIndex}
            nextImage={nextImage}
            prevImage={prevImage}
            setImageIndex={setImageIndex}
            openImageModal={openImageModal}
          />

          <ImageModal
            isOpen={isImageModalOpen}
            onClose={closeImageModal}
            panelImages={panelImages}
            currentImageIndex={currentImageIndex}
            nextImage={nextImage}
            prevImage={prevImage}
            setImageIndex={setImageIndex}
          />

          <PVTTechnology
            detailData={pvtDetailData}
            pvtImage1={pvtImage1}
            pvtImage={pvtImage}
          />

          <PVTBenefits detailData={pvtDetailData} />

          <PVTSpecifications detailData={pvtDetailData} />
          </div>
        </section>
      </main>
    </>
  );
});

export default PVT;
